import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { playMotion, prefersReducedMotion } from "./motion";

/* ------------------------------------------------------------------ *
 * مار پله — one PICTURE LAYER of the scene (the background, the temple's
 * cut-out), placed by W1's `sceneLayout` in CSS px.
 *
 * ⚠️ CONTRACT (card-86 round 2): batch W1 RENDERS this and never edits it;
 * its PROPS are the contract. Batch P owns the body (plan §8.8):
 *
 *   · THE FALLBACK IS REAL — the `<img src>` is ALWAYS the JPEG (or PNG); a
 *     WebP, when the art has one, only ADDS a `<source type="image/webp">`
 *     in a `<picture>`, so a phone that cannot show WebP shows the JPEG.
 *     No WebP → the bare `<img>`, exactly as round 1 drew it.
 *   · THE GAME NEVER WAITS FOR IT — the layer is hidden (opacity 0) until
 *     its `load`, so the board and the game show first over the scene's
 *     own edge-colour gradient (W1's `BASE_BACKGROUND`, the placeholder
 *     underneath), and the picture swaps in once it has arrived. Its box
 *     is fixed by the props, so a late picture shifts nothing.
 *   · THE SWAP IS ONE FINITE WEB ANIMATION — {@link SCENE_FADE_MS} on
 *     `opacity`, motion.ts's `playMotion` (the end state is committed
 *     inline, the animation is cancelled the moment it finishes): nothing
 *     runs afterwards, no CSS keyframes, no loop. Reduced motion or a
 *     hidden app → shown at once, no animation; a picture already decoded
 *     at mount (the cache, a remount) → shown at once.
 * ------------------------------------------------------------------ */

export interface ScenePictureProps {
  /** The JPEG (or PNG) fallback URL — Vite-imported, hashed. Always the `<img src>`. */
  readonly jpg: string;
  /** The WebP URL when the art has one (`<source type="image/webp">`, batch P). */
  readonly webp?: string;
  /** Where the layer goes, in CSS px, absolutely positioned in the scene's stage. */
  readonly box: {
    readonly left: number;
    readonly top: number;
    readonly width: number;
    readonly height: number;
  };
  /** W1's mask / feather class. */
  readonly className?: string;
  readonly testId?: string;
}

/** The fade-in's length — one short, finite opacity animation. */
export const SCENE_FADE_MS = 200;

/** Fade only when the user allows motion and the app is on screen. */
function canFade(): boolean {
  const hidden = typeof document !== "undefined" && document.hidden === true;
  return !hidden && !prefersReducedMotion();
}

export function ScenePicture(props: ScenePictureProps): ReactNode {
  const imgRef = useRef<HTMLImageElement>(null);
  // The picture SHOWN is the one whose sources loaded: a new source hides the layer again until
  // ITS load (the scene keeps its placeholder rather than a stale or half-drawn picture).
  const key = `${props.webp ?? ""}|${props.jpg}`;
  const [shownKey, setShownKey] = useState<string | null>(null);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (!img) return undefined;
    let stop: ((notify?: boolean) => void) | null = null;
    // Already decoded — a cached picture or a remount: shown at once, nothing to fade.
    if (img.complete && img.naturalWidth > 0) {
      setShownKey(key);
      return undefined;
    }
    const onLoad = (): void => {
      setShownKey(key);
      if (canFade()) {
        // The inline end state (opacity 1) is committed by the render above; the animation only
        // carries the layer up to it, then is cancelled — `playMotion`'s contract.
        stop = playMotion(img, [{ opacity: 0 }, { opacity: 1 }], SCENE_FADE_MS, () => {
          stop = null;
        });
      }
    };
    img.addEventListener("load", onLoad, { once: true });
    return () => {
      img.removeEventListener("load", onLoad);
      if (stop) stop(false);
    };
  }, [key]);

  const style: CSSProperties = {
    position: "absolute",
    left: props.box.left,
    top: props.box.top,
    width: props.box.width,
    height: props.box.height,
    pointerEvents: "none",
    opacity: shownKey === key ? 1 : 0,
  };
  const img = (
    <img
      ref={imgRef}
      src={props.jpg}
      alt=""
      aria-hidden
      decoding="async"
      draggable={false}
      className={props.className}
      data-testid={props.testId}
      style={style}
    />
  );
  if (!props.webp) return img;
  return (
    <picture>
      <source type="image/webp" srcSet={props.webp} />
      {img}
    </picture>
  );
}
